import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Dropdown,
    Header,
    Icon,
    Label,
    Loader,
    Message,
    Modal,
    Pagination,
    Segment,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import {
    getReportCalendar,
    getReportCalendarMonth,
    getReportTimeline,
} from "../../../services/reporting.service";
import {
    ReportCalendarMonthMetric,
    ReportCalendarMonthResponse,
    ReportCalendarResponse,
    ReportFilters,
    ReportTimelineResponse,
    SortDirection,
} from "../../../types/models/Reporting";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
} from "../reportingFormatters";

interface Props {
    open: boolean;
    filters: ReportFilters;
    origin?: string;
    onClose: () => void;
}

interface TrendPoint {
    key: number;
    label: string;
    shortLabel: string;
    totalAmount: number;
    orderCount: number;
}

interface TrendChartProps {
    points: TrendPoint[];
    selectedKey?: number;
}

type Period = number | "ALL";

const compactCurrency = new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    notation: "compact",
    maximumFractionDigits: 1,
});

const TrendChart = ({ points, selectedKey }: TrendChartProps) => {
    const width = Math.max(1100, points.length * 105);
    const height = 220;
    const left = 68;
    const right = 20;
    const top = 18;
    const bottom = 38;
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;
    const maximum = Math.max(...points.map((point) => Number(point.totalAmount)), 1);
    const x = (index: number) => left + (index * plotWidth) / Math.max(points.length - 1, 1);
    const y = (value: number) => top + plotHeight - (value / maximum) * plotHeight;
    const linePoints = points
        .map((point, index) => `${x(index)},${y(Number(point.totalAmount))}`)
        .join(" ");
    const area = points.length
        ? `${left},${top + plotHeight} ${linePoints} ${x(points.length - 1)},${top + plotHeight}`
        : "";

    if (!points.length) {
        return <Message info content="No hay periodos con ventas para construir la gráfica." />;
    }

    return (
        <div className="report-calendar-chart" aria-label="Tendencia de ventas">
            <svg viewBox={`0 0 ${width} ${height}`} role="img" style={{ minWidth: `${width}px` }}>
                <title>Tendencia de ventas</title>
                {[0, 0.5, 1].map((ratio) => {
                    const gridY = top + plotHeight * ratio;
                    const value = maximum * (1 - ratio);
                    return (
                        <g key={ratio}>
                            <line x1={left} x2={width - right} y1={gridY} y2={gridY} className="report-calendar-chart__grid" />
                            <text x={left - 9} y={gridY + 4} textAnchor="end" className="report-calendar-chart__axis">
                                {compactCurrency.format(value)}
                            </text>
                        </g>
                    );
                })}
                <polygon points={area} className="report-calendar-chart__area" />
                <polyline points={linePoints} className="report-calendar-chart__line" />
                {points.map((point, index) => (
                    <g key={point.key}>
                        <circle
                            cx={x(index)}
                            cy={y(Number(point.totalAmount))}
                            r={selectedKey === point.key ? 7 : 5}
                            className={selectedKey === point.key
                                ? "report-calendar-chart__point report-calendar-chart__point--active"
                                : "report-calendar-chart__point"}
                        >
                            <title>{`${point.label}: ${formatCurrency(point.totalAmount)} · ${formatNumber(point.orderCount)} proyectos`}</title>
                        </circle>
                        <text x={x(index)} y={height - 12} textAnchor="middle" className="report-calendar-chart__month">
                            {point.shortLabel}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
};

const SalesTrendModal = ({ open, filters, origin, onClose }: Props) => {
    const currentYear = new Date().getFullYear();
    const [period, setPeriod] = useState<Period>(currentYear);
    const [timeline, setTimeline] = useState<ReportTimelineResponse>();
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [calendar, setCalendar] = useState<ReportCalendarResponse>();
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<number>();
    const [monthData, setMonthData] = useState<ReportCalendarMonthResponse>();
    const [monthLoading, setMonthLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [sortProperty, setSortProperty] = useState("periodDate");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const detailRef = useRef<HTMLDivElement>(null);

    const yearOptions = useMemo(() => {
        const years = Array.from(new Set([currentYear, ...(timeline?.availableYears || [])]))
            .sort((left, right) => right - left);
        return [
            { key: "ALL", value: "ALL", text: "Todo el histórico", icon: "chart line" },
            ...years.map((year) => ({ key: year, value: year, text: year.toString() })),
        ];
    }, [currentYear, timeline?.availableYears]);

    useEffect(() => {
        if (!open) return;
        setPeriod(currentYear);
        setSelectedMonth(undefined);
        setMonthData(undefined);
        setPage(0);
    }, [open, currentYear]);

    useEffect(() => {
        if (!open) return;
        let mounted = true;
        const load = async () => {
            setTimelineLoading(true);
            try {
                const response = await getReportTimeline("SALES", filters, origin);
                if (mounted) setTimeline(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el histórico de ventas.");
            } finally {
                if (mounted) setTimelineLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [open, filters, origin]);

    useEffect(() => {
        if (!open || period === "ALL") return;
        let mounted = true;
        const load = async () => {
            setCalendarLoading(true);
            try {
                const response = await getReportCalendar("SALES", period, filters, origin);
                if (mounted) setCalendar(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el calendario de ventas.");
            } finally {
                if (mounted) setCalendarLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [open, period, filters, origin]);

    useEffect(() => {
        if (!open || period === "ALL" || !selectedMonth) return;
        let mounted = true;
        const load = async () => {
            setMonthLoading(true);
            try {
                const response = await getReportCalendarMonth(
                    "SALES",
                    period,
                    selectedMonth,
                    filters,
                    { page, size, sort: `${sortProperty},${sortDirection}` },
                    origin
                );
                if (mounted) setMonthData(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el detalle mensual.");
            } finally {
                if (mounted) setMonthLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [open, period, selectedMonth, filters, origin, page, size, sortProperty, sortDirection]);

    const selectPeriod = (value: Period) => {
        setPeriod(value);
        setSelectedMonth(undefined);
        setMonthData(undefined);
        setPage(0);
    };

    const selectMonth = (month: number) => {
        const monthChanged = selectedMonth !== month;
        setSelectedMonth(month);
        if (monthChanged) setMonthData(undefined);
        setPage(0);
        window.setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    };

    const changeSort = (property: string) => {
        setPage(0);
        setSortDirection((current) => sortProperty === property && current === "asc" ? "desc" : "asc");
        setSortProperty(property);
    };

    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;
    const allTime = period === "ALL";
    const cutoff = allTime ? timeline?.cutoff : calendar?.cutoff;
    const cutoffYear = Number(cutoff?.slice(0, 4));
    const cutoffMonth = Number(cutoff?.slice(5, 7));
    const isFutureMonth = (month: number) => !allTime
        && Number(period) === cutoffYear
        && month > cutoffMonth;
    const isCurrentMonth = (month: number) => !allTime
        && Number(period) === cutoffYear
        && month === cutoffMonth;
    const visibleMonths = calendar?.months.filter((month) => !isFutureMonth(month.month)) || [];
    const selectedMetric = calendar?.months.find((month) => month.month === selectedMonth);
    const summary = allTime ? timeline?.summary : calendar?.summary;
    const chartPoints: TrendPoint[] = allTime
        ? (timeline?.years || []).map((year) => ({
            key: year.year,
            label: year.year.toString(),
            shortLabel: year.year.toString(),
            totalAmount: year.totalAmount,
            orderCount: year.orderCount,
        }))
        : visibleMonths.map((month) => ({
            key: month.month,
            label: month.label,
            shortLabel: month.label.slice(0, 3),
            totalAmount: month.totalAmount,
            orderCount: month.orderCount,
        }));
    const loading = allTime ? timelineLoading : calendarLoading;

    return (
        <Modal open={open} onClose={onClose} size="fullscreen" closeIcon className="dashboard-fullscreen-modal report-calendar-modal">
            <Modal.Header>
                <div className="report-calendar-header">
                    <Header as="h3">
                        <Icon name="chart line" />
                        <Header.Content>
                            Tendencia de ventas
                            <Header.Subheader>Calendario mensual e histórico de proyectos terminados</Header.Subheader>
                        </Header.Content>
                    </Header>
                    <div>
                        <span>Periodo</span>
                        <Dropdown
                            className="report-calendar-period-dropdown"
                            selection
                            scrolling
                            value={period}
                            options={yearOptions}
                            onChange={(_, data) => selectPeriod(data.value === "ALL" ? "ALL" : Number(data.value))}
                        />
                    </div>
                </div>
            </Modal.Header>
            <Modal.Content scrolling>
                <Message
                    info
                    icon="info circle"
                    content={allTime
                        ? "La vista histórica compara los años con ventas. El año en curso es provisional y conserva los filtros globales y el origen."
                        : `El año ${period} reemplaza el rango global de fechas. Los demás filtros y el origen permanecen activos.`}
                />

                {loading && <Loader active inline="centered">Construyendo tendencia de ventas...</Loader>}
                {!loading && summary && (
                    <>
                        <div className="report-calendar-summary">
                            <div><span>Proyectos terminados</span><strong>{formatNumber(summary.orderCount)}</strong></div>
                            <div><span>{allTime ? "Venta histórica" : "Venta anual"}</span><strong>{formatCurrency(summary.totalAmount)}</strong></div>
                            <div><span>Cobrado al corte</span><strong>{formatCurrency(summary.paidAmount)}</strong></div>
                            <div><span>Saldo pendiente</span><strong>{formatCurrency(summary.outstandingBalance)}</strong></div>
                            <div><span>Ticket promedio</span><strong>{formatCurrency(summary.averageTicket)}</strong></div>
                            <div>
                                <span>{allTime ? "Años con ventas" : "Meses con ventas"}</span>
                                <strong>{formatNumber(allTime
                                    ? timeline?.years.filter((year) => year.orderCount > 0).length
                                    : calendar?.months.filter((month) => month.orderCount > 0).length)}</strong>
                            </div>
                        </div>

                        <Segment className={`report-calendar-trend${allTime ? " report-calendar-trend--history" : ""}`}>
                            <div className="dashboard-section-heading dashboard-section-heading--compact">
                                <div>
                                    <Header as="h3"><Icon name="chart line" /> {allTime ? "Evolución histórica" : "Evolución mensual"}</Header>
                                    <p>{allTime ? "Totales por año; la variación del año en curso compara ambos años al mismo día de corte." : "Ventas terminadas de enero a diciembre."}</p>
                                </div>
                                <Label basic color="yellow">{allTime ? "Todo el histórico" : period}</Label>
                            </div>
                            <TrendChart points={chartPoints} selectedKey={allTime ? undefined : selectedMonth} />
                        </Segment>

                        {allTime && (
                            <div className="report-calendar-year-strip" aria-label="Resumen por año">
                                {timeline?.years.map((year) => {
                                    const current = year.year === cutoffYear;
                                    const change = year.amountChangePercent;
                                    const tone = change == null || change === 0 ? "neutral" : change > 0 ? "up" : "down";
                                    return (
                                        <button type="button" key={year.year} onClick={() => selectPeriod(year.year)}>
                                            <span>{year.year}{current ? " · en curso" : ""}</span>
                                            <strong>{formatCurrency(year.totalAmount)}</strong>
                                            <small>{formatNumber(year.orderCount)} proyectos</small>
                                            <em className={`report-calendar-change--${tone}`}>
                                                {change == null
                                                    ? "Primer año comparable"
                                                    : `${change > 0 ? "+" : ""}${formatPercent(change)} vs. ${year.changeBasis === "YTD_TO_CUTOFF" ? "mismo periodo anterior" : "año anterior"}${current ? " · provisional" : ""}`}
                                            </em>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {!allTime && (
                            <div className="report-calendar-months">
                                {calendar?.months.map((month: ReportCalendarMonthMetric) => {
                                    const change = month.amountChangePercent;
                                    const future = isFutureMonth(month.month);
                                    const current = isCurrentMonth(month.month);
                                    const tone = future
                                        ? "future"
                                        : change == null || change === 0 ? "neutral" : change > 0 ? "up" : "down";
                                    const comparison = future
                                        ? "Mes por transcurrir"
                                        : change == null
                                            ? current ? "Mes en curso" : "Sin base comparable"
                                            : `${change > 0 ? "+" : ""}${formatPercent(change)} vs. mes anterior${current ? " · provisional" : ""}`;
                                    return (
                                        <button
                                            type="button"
                                            key={month.month}
                                            className={`report-calendar-month report-calendar-month--${tone}${selectedMonth === month.month ? " report-calendar-month--selected" : ""}`}
                                            onClick={() => selectMonth(month.month)}
                                            aria-pressed={selectedMonth === month.month}
                                            disabled={future}
                                        >
                                            <div>
                                                <span>{month.label}</span>
                                                <Icon name={future ? "clock outline" : tone === "up" ? "arrow up" : tone === "down" ? "arrow down" : "minus"} />
                                            </div>
                                            <strong>{future ? "—" : formatCurrency(month.totalAmount)}</strong>
                                            <small>{future ? "Sin periodo transcurrido" : `${formatNumber(month.orderCount)} proyectos`}</small>
                                            <em>{comparison}</em>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {!allTime && (
                    <div ref={detailRef} className="report-calendar-detail">
                        {selectedMonth && (
                            <Segment>
                                <div className="dashboard-section-heading dashboard-section-heading--compact">
                                    <div>
                                        <span className="dashboard-eyebrow">Detalle mensual</span>
                                        <Header as="h3">{selectedMetric?.label} {period}</Header>
                                        <p>{formatNumber(selectedMetric?.orderCount)} proyectos · {formatCurrency(selectedMetric?.totalAmount)}</p>
                                    </div>
                                    <Button basic icon="close" content="Ocultar detalle" onClick={() => setSelectedMonth(undefined)} />
                                </div>

                                {monthLoading && <Loader active inline="centered">Consultando proyectos del mes...</Loader>}
                                {!monthLoading && monthData?.orders.length === 0 && (
                                    <Message info content="No existen proyectos terminados para este mes." />
                                )}
                                {!monthLoading && Boolean(monthData?.orders.length) && (
                                    <div className="dashboard-table-scroll">
                                        <Table sortable striped compact selectable className="dashboard-table dashboard-report-table">
                                            <Table.Header><Table.Row>
                                                <Table.HeaderCell sorted={sorted("invoice")} onClick={() => changeSort("invoice")}>Factura</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("periodDate")} onClick={() => changeSort("periodDate")}>Terminación</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("status")} onClick={() => changeSort("status")}>Estado</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("clientName")} onClick={() => changeSort("clientName")}>Cliente</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("productName")} onClick={() => changeSort("productName")}>Producto</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("advisorName")} onClick={() => changeSort("advisorName")}>Asesor</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("totalAmount")} onClick={() => changeSort("totalAmount")} textAlign="right">Total</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("paidAmount")} onClick={() => changeSort("paidAmount")} textAlign="right">Cobrado</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("outstandingBalance")} onClick={() => changeSort("outstandingBalance")} textAlign="right">Saldo</Table.HeaderCell>
                                                <Table.HeaderCell sorted={sorted("collectionRatePercent")} onClick={() => changeSort("collectionRatePercent")} textAlign="right">Cobranza</Table.HeaderCell>
                                                <Table.HeaderCell />
                                            </Table.Row></Table.Header>
                                            <Table.Body>{monthData?.orders.map((order) => (
                                                <Table.Row key={order.orderId} warning={order.outstandingBalance > 0}>
                                                    <Table.Cell><strong>#{order.invoice || "—"}</strong></Table.Cell>
                                                    <Table.Cell>{formatReportDate(order.periodDate, true)}</Table.Cell>
                                                    <Table.Cell>
                                                        <Label color={orderStatusColor(order.status)}>
                                                            {orderStatusLabel(order.status, order.statusLabel)}
                                                        </Label>
                                                    </Table.Cell>
                                                    <Table.Cell>{order.clientName || "Sin cliente"}</Table.Cell>
                                                    <Table.Cell><strong>{order.productName || "Sin producto"}</strong><small>{order.productSku || "Sin SKU"}</small></Table.Cell>
                                                    <Table.Cell>{order.advisorName || "Sin asignar"}</Table.Cell>
                                                    <Table.Cell textAlign="right">{formatCurrency(order.totalAmount)}</Table.Cell>
                                                    <Table.Cell textAlign="right">{formatCurrency(order.paidAmount)}</Table.Cell>
                                                    <Table.Cell textAlign="right"><strong>{formatCurrency(order.outstandingBalance)}</strong></Table.Cell>
                                                    <Table.Cell textAlign="right">{formatPercent(order.collectionRatePercent)}</Table.Cell>
                                                    <Table.Cell collapsing>
                                                        <Button
                                                            as="a"
                                                            secondary
                                                            icon="search"
                                                            size="mini"
                                                            href={`/orders/${order.orderId}/details`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        />
                                                    </Table.Cell>
                                                </Table.Row>
                                            ))}</Table.Body>
                                        </Table>
                                    </div>
                                )}

                                <div className="dashboard-pagination dashboard-pagination--split">
                                    <span>{formatNumber(monthData?.totalElements)} proyectos</span>
                                    <Pagination
                                        activePage={page + 1}
                                        totalPages={Math.max(monthData?.totalPages || 1, 1)}
                                        onPageChange={(_, data) => setPage(Number(data.activePage) - 1)}
                                    />
                                    <Dropdown
                                        compact
                                        selection
                                        value={size}
                                        options={[10, 25, 50, 100].map((value) => ({ key: value, value, text: `${value} filas` }))}
                                        onChange={(_, data) => { setPage(0); setSize(Number(data.value)); }}
                                    />
                                </div>
                            </Segment>
                        )}
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions><Button onClick={onClose}><Icon name="cancel" /> Cerrar</Button></Modal.Actions>
        </Modal>
    );
};

export default SalesTrendModal;
